// frontend/src/actions/adminOrderActions.js

import * as actionTypes from "../constants/adminOrderConstants";
import api from "../utils/axiosInstance";
import { getApiErrorMessage } from "../utils/errorMessages";

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeUpper(value = "") {
  return safeText(value).toUpperCase();
}

function normalizeApiError(error) {
  return {
    message: getApiErrorMessage(error, {
      fallback:
        "We could not update admin orders right now. Please try again later.",
    }),
    code: safeText(error?.response?.data?.code || error?.errorCode || ""),
    requestId: safeText(
      error?.response?.data?.requestId || error?.requestId || "",
    ),
    retryAfterSeconds: Number(
      error?.response?.data?.retryAfterSeconds || error?.retryAfterSeconds || 0,
    ),
    payload: error?.response?.data || null,
  };
}

const ADMIN_BASE = "/admin/orders";

export const listAdminOrders =
  (params = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_ORDER_LIST_REQUEST });

    try {
      const { data } = await api.get(ADMIN_BASE, { params });

      dispatch({
        type: actionTypes.ADMIN_ORDER_LIST_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_ORDER_LIST_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const getAdminOrderByNumber = (orderNumber) => async (dispatch) => {
  dispatch({ type: actionTypes.ADMIN_ORDER_DETAILS_REQUEST });

  try {
    const { data } = await api.get(
      `${ADMIN_BASE}/${encodeURIComponent(normalizeUpper(orderNumber))}`,
    );

    dispatch({
      type: actionTypes.ADMIN_ORDER_DETAILS_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(error);

    dispatch({
      type: actionTypes.ADMIN_ORDER_DETAILS_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const getAdminOrderAnalytics = () => async (dispatch) => {
  dispatch({ type: actionTypes.ADMIN_ORDER_ANALYTICS_REQUEST });

  try {
    const { data } = await api.get(`${ADMIN_BASE}/analytics`);

    dispatch({
      type: actionTypes.ADMIN_ORDER_ANALYTICS_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(error);

    dispatch({
      type: actionTypes.ADMIN_ORDER_ANALYTICS_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const updateAdminOrderStatus =
  ({ orderNumber, status, reason = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_ORDER_STATUS_UPDATE_REQUEST });

    try {
      const { data } = await api.patch(
        `${ADMIN_BASE}/${encodeURIComponent(normalizeUpper(orderNumber))}/status`,
        { status, reason },
      );

      dispatch({
        type: actionTypes.ADMIN_ORDER_STATUS_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_ORDER_STATUS_UPDATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const updateAdminOrderFulfillment =
  ({ orderNumber, status, reason = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_ORDER_FULFILLMENT_UPDATE_REQUEST });

    try {
      const { data } = await api.patch(
        `${ADMIN_BASE}/${encodeURIComponent(
          normalizeUpper(orderNumber),
        )}/fulfillment`,
        { status, reason },
      );

      dispatch({
        type: actionTypes.ADMIN_ORDER_FULFILLMENT_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_ORDER_FULFILLMENT_UPDATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const updateAdminOrderCutStatus =
  ({ orderNumber, lineId = "", cutStatus, reason = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_ORDER_CUT_STATUS_UPDATE_REQUEST });

    try {
      const { data } = await api.patch(
        `${ADMIN_BASE}/${encodeURIComponent(
          normalizeUpper(orderNumber),
        )}/cut-status`,
        { lineId, cutStatus, reason },
      );

      dispatch({
        type: actionTypes.ADMIN_ORDER_CUT_STATUS_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_ORDER_CUT_STATUS_UPDATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const updateAdminManualPaymentStatus =
  ({ orderNumber, status, amountCaptured, failureCode, failureMessage } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_ORDER_MANUAL_PAYMENT_UPDATE_REQUEST });

    try {
      const { data } = await api.patch(
        `${ADMIN_BASE}/${encodeURIComponent(
          normalizeUpper(orderNumber),
        )}/payment/manual`,
        {
          status,
          amountCaptured,
          failureCode,
          failureMessage,
        },
      );

      dispatch({
        type: actionTypes.ADMIN_ORDER_MANUAL_PAYMENT_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_ORDER_MANUAL_PAYMENT_UPDATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const cancelAdminOrder =
  ({ orderNumber, ...payload } = {}) => async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_ORDER_STATUS_UPDATE_REQUEST });
    try {
      const { data } = await api.post(`${ADMIN_BASE}/${encodeURIComponent(normalizeUpper(orderNumber))}/cancel`, payload);
      dispatch({ type: actionTypes.ADMIN_ORDER_STATUS_UPDATE_SUCCESS, payload: data });
      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);
      dispatch({ type: actionTypes.ADMIN_ORDER_STATUS_UPDATE_FAIL, payload: payloadError });
      throw payloadError;
    }
  };

export const refundAdminOrder =
  ({ orderNumber, amount, reason = "", restoreInventory = false, textileOverride = false, idempotencyKey = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_ORDER_REFUND_REQUEST });

    try {
      const { data } = await api.post(
        `${ADMIN_BASE}/${encodeURIComponent(normalizeUpper(orderNumber))}/refund`,
        {
          amount,
          reason,
          restoreInventory,
          textileOverride,
          idempotencyKey,
        },
      );

      dispatch({
        type: actionTypes.ADMIN_ORDER_REFUND_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_ORDER_REFUND_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const listAdminReturnRequests =
  (params = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_RETURN_LIST_REQUEST });

    try {
      const { data } = await api.get(`${ADMIN_BASE}/returns/list`, {
        params,
      });

      dispatch({
        type: actionTypes.ADMIN_RETURN_LIST_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_RETURN_LIST_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const getAdminReturnRequestById =
  (returnRequestId) => async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_RETURN_DETAILS_REQUEST });

    try {
      const { data } = await api.get(
        `${ADMIN_BASE}/returns/${encodeURIComponent(safeText(returnRequestId))}`,
      );

      dispatch({
        type: actionTypes.ADMIN_RETURN_DETAILS_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_RETURN_DETAILS_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const approveAdminReturnRequest =
  ({ returnRequestId, note = "", items = [] } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_RETURN_APPROVE_REQUEST });

    try {
      const { data } = await api.patch(
        `${ADMIN_BASE}/returns/${encodeURIComponent(
          safeText(returnRequestId),
        )}/approve`,
        { note, items },
      );

      dispatch({
        type: actionTypes.ADMIN_RETURN_APPROVE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_RETURN_APPROVE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const rejectAdminReturnRequest =
  ({ returnRequestId, reason = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_RETURN_REJECT_REQUEST });

    try {
      const { data } = await api.patch(
        `${ADMIN_BASE}/returns/${encodeURIComponent(
          safeText(returnRequestId),
        )}/reject`,
        { reason },
      );

      dispatch({
        type: actionTypes.ADMIN_RETURN_REJECT_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_RETURN_REJECT_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const markAdminReturnReceived =
  ({ returnRequestId, note = "", items = [] } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_RETURN_RECEIVED_REQUEST });

    try {
      const { data } = await api.patch(
        `${ADMIN_BASE}/returns/${encodeURIComponent(
          safeText(returnRequestId),
        )}/received`,
        { note, items },
      );

      dispatch({
        type: actionTypes.ADMIN_RETURN_RECEIVED_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_RETURN_RECEIVED_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const refundAdminReturnRequest =
  ({ returnRequestId, amount, reason = "", restoreInventory = false, idempotencyKey = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADMIN_RETURN_REFUND_REQUEST });

    try {
      const { data } = await api.post(
        `${ADMIN_BASE}/returns/${encodeURIComponent(
          safeText(returnRequestId),
        )}/refund`,
        { amount, reason, restoreInventory, idempotencyKey },
      );

      dispatch({
        type: actionTypes.ADMIN_RETURN_REFUND_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(error);

      dispatch({
        type: actionTypes.ADMIN_RETURN_REFUND_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const resetAdminOrderError = () => ({
  type: actionTypes.ADMIN_ORDER_RESET_ERROR,
});

export const resetAdminOrderMeta = () => ({
  type: actionTypes.ADMIN_ORDER_RESET_META,
});

export const resetAdminOrderState = () => ({
  type: actionTypes.ADMIN_ORDER_RESET_STATE,
});
